import type { CSSProperties, ReactElement } from 'react'
import { Form, RadioButtonField } from '@epages/react-components'
import { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'
import cc from 'classcat'
import loadable from '@loadable/component'

import type { BackgroundColorSettings, BackgroundWidthSettings } from './Block'
import { defaultBackgroundColorSettings, getDefaultBackgroundWidthSettings } from './Block'
import { themeGroupDelimiter } from '../../../../utils/themes'
import { useColorOptions } from '../../../utils/hooks/colorSettings'
import Button from '../../formComponents/Button'
import ButtonRow from '../../formComponents/ButtonRow'
import FormGroup from '../../formComponents/FormGroup'
import ImageField from './plugins/image/ImageField'
import useHelpCenterLink from '../../../utils/hooks/useHelpCenterLink'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ './SettingsLayer'))
const ColorField = loadable(() => import(/* webpackChunkName: "editor" */ '../../formComponents/ColorField'), {
  resolveComponent: (components) => components.ColorFieldRaw,
})

type Props = {
  currentColorSettings: BackgroundColorSettings
  currentWidthSettings: BackgroundWidthSettings
  onColorSettingsChange: (settings: BackgroundColorSettings) => void
  onWidthSettingsChange: (settings: BackgroundWidthSettings) => void
  onCancel: () => void
  onSave: () => void
  config: {
    plugins?: Record<
      string,
      {
        config?: {
          storeFile?: (file: File) => Promise<any>
          imageUrl?: (url: string) => string
          previewUrl?: (url: string) => string
        }
      }
    >
  }
  themeName: string
} & TranslateProps

export default function BlockBackgroundSettings({
  t,
  currentColorSettings,
  currentWidthSettings,
  onColorSettingsChange,
  onWidthSettingsChange,
  onSave,
  onCancel,
  config,
  themeName,
}: Readonly<Props>): ReactElement {
  const [showBlockBackgroundSettings, setShowBlockBackgroundSettings] = useState(false)
  const [activeTab, setActiveTab] = useState<'backgroundStyle' | 'backgroundWidth'>('backgroundStyle')
  const [imageError, setImageError] = useState<(Error & { code: number }) | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')
  const isUnsupportedMediaType = imageError?.code === 415

  const {
    backgroundColor,
    backgroundImage,
    backgroundImageAlignment,
    backgroundType,
    gradientEndColor,
    gradientMidColor,
    gradientOrientation,
    gradientStartColor,
    showGradientMidColor,
    backgroundOverlayOpacity,
  } = currentColorSettings
  const imageConfig = config?.plugins?.['epages.image']?.config
  const tBackground = (key: string) => t(`backgroundButton.settingsLayer.backgroundStyleRadioButtons.${key}`)

  const themeNameForLanguageKeys = useSelector<State, string>((state) =>
    state.getIn(['shop', 'theme', 'name'], '').replace('.', '_'),
  )
  const [themeGroupName] = themeNameForLanguageKeys.split(themeGroupDelimiter)
  const tThemeSettings = (subKey: string) => t(`themes:${themeGroupName}.settings.${subKey}`)
  const tColorOptions = (subKey: string) => t(`components.colorFieldComponent.popover.colorPresets.${subKey}.tooltip`)
  const colorOptions = useColorOptions(tColorOptions, tThemeSettings)

  const saveColor = () => {
    setShowBlockBackgroundSettings(false)
    setActiveTab('backgroundStyle')
    onSave()
  }

  const cancelHandler = () => {
    onCancel()
    setShowBlockBackgroundSettings(false)
    setActiveTab('backgroundStyle')
  }

  const handleSwitch = (event) => {
    event.preventDefault()
    onColorSettingsChange({
      ...currentColorSettings,
      gradientStartColor: gradientEndColor,
      gradientEndColor: gradientStartColor,
    })
  }

  const toggleColorSettings = () => {
    if (showBlockBackgroundSettings) {
      cancelHandler()
    } else {
      setShowBlockBackgroundSettings(true)
    }
  }

  const handleReset = () => {
    onColorSettingsChange(defaultBackgroundColorSettings)
  }

  const handleSolidColorSelection = () => {
    const newColorSettings = { ...currentColorSettings, backgroundType: 'color' as const }
    onColorSettingsChange(newColorSettings)

    // If switching to solid color and it's transparent, reset width to theme default
    if (backgroundColor === 'transparent' || backgroundColor === defaultBackgroundColorSettings.backgroundColor) {
      const defaults = getDefaultBackgroundWidthSettings(themeName)
      onWidthSettingsChange({
        ...defaults,
        isUserModified: false,
      })
    }
  }

  const handleBackgroundImageChange = (data: ImmutableMap) => {
    const imageData = data.get('backgroundImage') || Immutable.Map()
    const nextBackgroundImage = {
      src: imageData.get('src', ''),
      width: imageData.get('width', null),
      height: imageData.get('height', null),
    }
    const nextBackgroundImageAlignment = data.get('backgroundImageAlignment', backgroundImageAlignment)
    onColorSettingsChange({
      ...currentColorSettings,
      backgroundImage: nextBackgroundImage,
      backgroundImageAlignment: nextBackgroundImageAlignment,
    })
  }

  const backgroundImageValue = useMemo(
    () =>
      Immutable.Map({
        backgroundImage: Immutable.Map({
          src: backgroundImage?.src ?? defaultBackgroundColorSettings.backgroundImage.src,
          width: backgroundImage?.width ?? defaultBackgroundColorSettings.backgroundImage.width,
          height: backgroundImage?.height ?? defaultBackgroundColorSettings.backgroundImage.height,
        }),
        backgroundImageAlignment: backgroundImageAlignment ?? defaultBackgroundColorSettings.backgroundImageAlignment,
      }),
    [backgroundImage?.src, backgroundImage?.width, backgroundImage?.height, backgroundImageAlignment],
  )

  const handleWidthChange = (width: 'content' | 'full') => {
    onWidthSettingsChange({
      ...currentWidthSettings,
      width,
      isUserModified: true,
    })
  }

  const imageBackgroundDisabled = !backgroundImage?.src
  const overlayOpacitySliderValue = backgroundOverlayOpacity ?? 0

  const imageAlignmentRadioButtons = [
    {
      label: tBackground('image.imageAlignment.options.top'),
      value: 'top',
    },
    {
      label: tBackground('image.imageAlignment.options.bottom'),
      value: 'bottom',
    },
    {
      label: tBackground('image.imageAlignment.options.center'),
      value: 'center',
    },
  ]

  const styleTabBody = (
    <>
      <div className="background-style-wrapper">
        <div className={cc(['background-style-container', { active: backgroundType === 'color' }])}>
          <label
            className="ep-form-row-label background-style-label"
            htmlFor="background-style-solid"
            data-testid="background-style-solid"
          >
            <input
              type="radio"
              id="background-style-solid"
              checked={backgroundType === 'color'}
              onChange={handleSolidColorSelection}
            />
            <span>{tBackground('solid.label')}</span>
          </label>
          <div
            className="background-style-expanded-container"
            data-testid="background-style-expanded-solid"
            aria-hidden={backgroundType !== 'color'}
            inert={backgroundType !== 'color' ? ('' as any) : undefined}
          >
            <div className="background-style-expanded-content ep-form-row-label">
              <ColorField
                value={backgroundColor}
                side="left"
                className="ep-color-field-box"
                presetColors={colorOptions.toJS()}
                offsetDistance={20}
                resetValue={defaultBackgroundColorSettings.backgroundColor}
                onChange={(backgroundColor = defaultBackgroundColorSettings.backgroundColor) =>
                  onColorSettingsChange({ ...currentColorSettings, backgroundColor })
                }
              />
              <span>{tBackground('solid.colorPicker.label')}</span>
            </div>
          </div>
        </div>
        <div className={cc(['background-style-container', { active: backgroundType === 'gradient' }])}>
          <label
            className="ep-form-row-label background-style-label"
            htmlFor="background-style-gradient"
            data-testid="background-style-gradient"
          >
            <input
              type="radio"
              id="background-style-gradient"
              checked={backgroundType === 'gradient'}
              onChange={() => onColorSettingsChange({ ...currentColorSettings, backgroundType: 'gradient' })}
            />
            <span>{tBackground('gradient.label')}</span>
          </label>
          <div
            className="background-style-expanded-container"
            data-testid="background-style-expanded-gradient"
            aria-hidden={backgroundType !== 'gradient'}
            inert={backgroundType !== 'gradient' ? ('' as any) : undefined}
          >
            <div className="background-style-expanded-content gradient-background">
              <fieldset>
                <legend className="sr-only">{tBackground('gradient.numberRadioButtons.accessibilityLabel')}</legend>
                <label htmlFor="twoColors">
                  <input
                    type="radio"
                    id="twoColors"
                    checked={!showGradientMidColor}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: false })}
                  />
                  {tBackground('gradient.numberRadioButtons.twoColors')}
                </label>
                <label htmlFor="threeColors">
                  <input
                    type="radio"
                    id="threeColors"
                    data-testid="threeColors"
                    checked={showGradientMidColor}
                    onChange={() => onColorSettingsChange({ ...currentColorSettings, showGradientMidColor: true })}
                  />
                  {tBackground('gradient.numberRadioButtons.threeColors')}
                </label>
              </fieldset>
              <button className="gradient-direction-switch" onClick={handleSwitch}>
                {tBackground('gradient.switchColorsButton.label')}
              </button>
              <div
                className="gradient-bar"
                style={{
                  background: showGradientMidColor
                    ? `linear-gradient( 90deg, ${gradientStartColor}, ${gradientMidColor}, ${gradientEndColor})`
                    : `linear-gradient( 90deg, ${gradientStartColor}, ${gradientEndColor})`,
                }}
              ></div>
              <div className={`gradient-colors ${showGradientMidColor ? 'gradient-colors-three' : ''}`}>
                <ColorField
                  value={gradientStartColor}
                  className="ep-color-field-box"
                  presetColors={colorOptions.toJS()}
                  offsetDistance={20}
                  side="left"
                  align="end"
                  data-testid="gradient-start-color"
                  resetValue={defaultBackgroundColorSettings.gradientStartColor}
                  onChange={(gradientStartColor = defaultBackgroundColorSettings.gradientStartColor) =>
                    onColorSettingsChange({ ...currentColorSettings, gradientStartColor })
                  }
                />
                {showGradientMidColor && (
                  <ColorField
                    value={gradientMidColor}
                    className="ep-color-field-box"
                    presetColors={colorOptions.toJS()}
                    offsetDistance={20}
                    resetValue={defaultBackgroundColorSettings.gradientMidColor}
                    data-testid="gradient-mid-color"
                    onChange={(gradientMidColor = defaultBackgroundColorSettings.gradientMidColor) =>
                      onColorSettingsChange({ ...currentColorSettings, gradientMidColor })
                    }
                    side="right"
                    align="end"
                  />
                )}
                <ColorField
                  value={gradientEndColor}
                  className="ep-color-field-box"
                  presetColors={colorOptions.toJS()}
                  offsetDistance={20}
                  resetValue={defaultBackgroundColorSettings.gradientEndColor}
                  data-testid="gradient-end-color"
                  onChange={(gradientEndColor = defaultBackgroundColorSettings.gradientEndColor) =>
                    onColorSettingsChange({ ...currentColorSettings, gradientEndColor })
                  }
                  side="right"
                  align="end"
                />
              </div>
              <fieldset className="gradient-orientation">
                <legend className="gradient-orientation-legend ep-form-row-label">
                  {tBackground('gradient.orientationRadioButtons.label')}
                </legend>
                <div className="gradient-orientation-options">
                  <span>
                    <input
                      type="radio"
                      id="horizontal"
                      checked={gradientOrientation === '90deg'}
                      onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '90deg' })}
                    />
                    <label htmlFor="horizontal">{tBackground('gradient.orientationRadioButtons.horizontal')}</label>
                  </span>
                  <span>
                    <input
                      type="radio"
                      id="vertical"
                      checked={gradientOrientation === '180deg'}
                      onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '180deg' })}
                    />
                    <label htmlFor="vertical">{tBackground('gradient.orientationRadioButtons.vertical')}</label>
                  </span>
                  <span>
                    <input
                      type="radio"
                      id="diagonal"
                      checked={gradientOrientation === '45deg'}
                      onChange={() => onColorSettingsChange({ ...currentColorSettings, gradientOrientation: '45deg' })}
                    />
                    <label htmlFor="diagonal">{tBackground('gradient.orientationRadioButtons.diagonal')}</label>
                  </span>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div className={cc(['background-style-container', { active: backgroundType === 'image' }])}>
          <label
            className="ep-form-row-label background-style-label"
            htmlFor="background-style-image"
            data-testid="background-style-image"
          >
            <input
              type="radio"
              id="background-style-image"
              checked={backgroundType === 'image'}
              onChange={() => onColorSettingsChange({ ...currentColorSettings, backgroundType: 'image' })}
            />
            <span>{tBackground('image.label')}</span>
          </label>
          <div
            className="background-style-expanded-container"
            data-testid="background-style-expanded-image"
            aria-hidden={backgroundType !== 'image'}
            inert={backgroundType !== 'image' ? ('' as any) : undefined}
          >
            <div className="background-style-expanded-content">
              {imageError && (
                <div className="dali-settingslayer-element">
                  <div className="dali-notification-danger">
                    {imageError.message}{' '}
                    {isUnsupportedMediaType && (
                      <a
                        href={helpCenterUrl}
                        target="_blank"
                        className="ep-form-row-text-external-link"
                        rel="noreferrer noopener"
                      >
                        {t(':components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
                      </a>
                    )}
                  </div>
                </div>
              )}
              <Form name="blockBackgroundImage" value={backgroundImageValue} onChange={handleBackgroundImageChange}>
                <div className="ep-form-row">
                  <ImageField
                    name="backgroundImage"
                    storeFile={imageConfig?.storeFile}
                    imageUrl={imageConfig?.imageUrl}
                    previewUrl={imageConfig?.previewUrl}
                    showDeleteButton
                    className="dali-settingslayer-element"
                    onError={(error) => setImageError(error)}
                  />
                </div>
                <FormGroup
                  name="backgroundImageAlignment"
                  label={tBackground('image.imageAlignment.label')}
                  className={cc([{ disabled: !backgroundImage?.src }])}
                >
                  <RadioButtonField
                    className="image-background-alignment"
                    name="backgroundImageAlignment"
                    buttons={imageAlignmentRadioButtons}
                    disabled={!backgroundImage?.src}
                  />
                </FormGroup>
                <div className={cc(['ep-form-row', { disabled: imageBackgroundDisabled }])}>
                  <label htmlFor="background-overlay-opacity" className="dali-settingslayer-label">
                    {tBackground('image.overlayOpacitySlider.label')}
                  </label>
                  <div className="dali-range-input-with-numeric">
                    <input
                      id="background-overlay-opacity"
                      type="range"
                      className="dali-range-input"
                      min={0}
                      max={100}
                      step={1}
                      value={overlayOpacitySliderValue}
                      disabled={imageBackgroundDisabled}
                      style={
                        {
                          '--min': 0,
                          '--max': 100,
                          '--val': overlayOpacitySliderValue,
                        } as CSSProperties
                      }
                      onChange={(e) =>
                        onColorSettingsChange({
                          ...currentColorSettings,
                          backgroundOverlayOpacity: Number(e.target.value),
                        })
                      }
                    />
                    <input
                      type="number"
                      className="dali-range-output"
                      min={0}
                      max={100}
                      step={1}
                      value={overlayOpacitySliderValue}
                      disabled={imageBackgroundDisabled}
                      onChange={(e) => {
                        const val = Number(e.target.value)
                        if (val >= 0 && val <= 100) {
                          onColorSettingsChange({
                            ...currentColorSettings,
                            backgroundOverlayOpacity: val,
                          })
                        }
                      }}
                    />
                  </div>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
      <button type="button" className="reset-button" onClick={handleReset}>
        {t('backgroundButton.settingsLayer.resetButton.label')}
      </button>
    </>
  )

  const widthTabBody = (
    <fieldset>
      <legend className="ep-form-row-small-margin ep-form-row-label">
        {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.label')}
      </legend>
      <div className="background-width-options">
        <label>
          <input
            type="radio"
            id="content-width"
            checked={currentWidthSettings.width === 'content'}
            onChange={() => handleWidthChange('content')}
          />
          {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.contentWidthRadioButton')}
        </label>
        <label>
          <input
            type="radio"
            id="full-width"
            checked={currentWidthSettings.width === 'full'}
            onChange={() => handleWidthChange('full')}
          />
          {t('backgroundButton.settingsLayer.backgroundWidthRadioButtons.fullWidthRadioButton')}
        </label>
      </div>
    </fieldset>
  )

  const tabs = [
    {
      name: 'backgroundStyle',
      title: t('backgroundButton.settingsLayer.backgroundStyle.title'),
      active: activeTab === 'backgroundStyle',
      onClick: (name: string) => setActiveTab(name as 'backgroundStyle' | 'backgroundWidth'),
    },
    {
      name: 'backgroundWidth',
      title: t('backgroundButton.settingsLayer.backgroundWidth.title'),
      active: activeTab === 'backgroundWidth',
      onClick: (name: string) => setActiveTab(name as 'backgroundStyle' | 'backgroundWidth'),
    },
  ]
  const footer = (
    <ButtonRow>
      <Button type="button" variant="outline" onClick={cancelHandler}>
        {t('backgroundButton.settingsLayer.cancelButton.label')}
      </Button>
      <Button type="button" onClick={saveColor}>
        {t('backgroundButton.settingsLayer.saveButton.label')}
      </Button>
    </ButtonRow>
  )

  return (
    <>
      <button
        ref={buttonRef}
        className="dali-block-actionbar-button dali-block-actionbar-button-background-color"
        title={t('backgroundButton.label')}
        onClick={() => toggleColorSettings()}
      />
      <SettingsLayer
        open={showBlockBackgroundSettings}
        className="dali-settingslayer-block-background"
        virtualRef={buttonRef}
        side="right"
        onEscapeKeyDown={cancelHandler}
      >
        {({ renderLayout }) =>
          renderLayout({
            title: t(`backgroundButton.settingsLayer.${activeTab}.title`),
            tabs,
            body: activeTab === 'backgroundStyle' ? styleTabBody : widthTabBody,
            footer,
          })
        }
      </SettingsLayer>
    </>
  )
}
